'use client';
import React, {useCallback, useEffect, useState} from 'react'
import {Button} from '@mui/material'
import Loader from '../../core/components/Loader'
import {IColumn, Table} from '../../core/components/Table'
import {IResource} from '../../core/interfaces'
import {deleteResource, getAllResources} from '../../services/resources'
import {ResourceModal} from './ResourceModal'
import {MessageBox} from '../../core/components/MessageBox'

export const ResourcesListPage: React.FC = (): JSX.Element => {
    const [resources, setResources] = useState<IResource[]>([])
    const [loading, setLoading] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [editResource, setEditResource] = useState<IResource | null>(null)
    const [showMessageBox, setShowMessageBox] = useState(false)
    const [message, setMessage] = useState('')
    const [isError, setIsError] = useState(false)

    const loadResources = useCallback(async () => {
        setLoading(true)
        try {
            const response = await getAllResources()
            setResources(response.data)
        } catch (e) {
            setIsError(true)
            setShowMessageBox(true)
            setMessage('Failed to load resources')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        loadResources()
    }, [loadResources])

    const handleDelete = async (id: string) => {
        try {
            await deleteResource(id)
            await loadResources()
        } catch {
            setIsError(true)
            setShowMessageBox(true)
            setMessage('Failed to delete resource')
        }
    }

    const columns: IColumn[] = [
        {
            title: 'Name',
            accessor: 'name',
        },
        {
            title: 'Type',
            accessor: 'type',
            render: (row: IResource) => <span>{row.type.replace(/_/g, ' ')}</span>,
        },
        {
            title: 'File Key',
            accessor: 'key',
            render: (row: IResource) => <span style={{fontSize: '12px', wordBreak: 'break-all'}}>{row.key}</span>,
        },
        {
            title: 'Edit',
            accessor: '',
            render: (row: IResource) => (
                <div
                    style={{cursor: 'pointer'}}
                    className="view"
                    onClick={() => {
                        setEditResource(row)
                        setShowModal(true)
                    }}
                >
                    Edit
                </div>
            ),
        },
        {
            title: 'Delete',
            accessor: '',
            render: (row: IResource) => (
                <Button
                    className="btn btn-secondary"
                    onDoubleClick={() => handleDelete(row.id as string)}
                >
                    Delete
                </Button>
            ),
        },
    ]

    return (
        <>
            <div style={{padding: 20, backgroundColor: '#F8F8F8', position: 'relative'}}>
                {loading && <Loader />}
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20}}>
                    <h2>Resources</h2>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                            setEditResource(null)
                            setShowModal(true)
                        }}
                    >
                        Create Resource
                    </Button>
                </div>
                <Table columns={columns} data={resources} />
            </div>

            <ResourceModal
                show={showModal}
                onClose={() => setShowModal(false)}
                onSaved={loadResources}
                editResource={editResource}
            />

            <MessageBox
                message={message}
                setShowMessageBox={setShowMessageBox}
                showMessageBox={showMessageBox}
                isError={isError}
            />
        </>
    )
}
